import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../../components/ui/Header';
import Icon from '../../../components/AppIcon';

const AuditTrail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  const [dateFilter, setDateFilter] = useState('today');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock audit trail data
  const auditLogs = [
    {
      id: 'AUDIT-001',
      timestamp: new Date(Date.now() - 300000),
      user: 'Sarah Johnson',
      userEmail: 'sarah.johnson@moneytransfer.com',
      action: 'Transaction Approved',
      targetId: 'TXN-2024-001',
      targetType: 'Transaction',
      details: 'Approved high-value transfer of $15,000',
      ipAddress: '192.168.1.100',
      severity: 'info'
    },
    {
      id: 'AUDIT-002',
      timestamp: new Date(Date.now() - 600000),
      user: 'Michael Chen',
      userEmail: 'michael.chen@moneytransfer.com',
      action: 'Document Rejected',
      targetId: 'DOC-2024-005',
      targetType: 'Document',
      details: 'Rejected passport verification - poor image quality',
      ipAddress: '192.168.1.105',
      severity: 'warning'
    },
    {
      id: 'AUDIT-003',
      timestamp: new Date(Date.now() - 900000),
      user: 'System',
      userEmail: 'system@moneytransfer.com',
      action: 'Alert Generated',
      targetId: 'ALERT-003',
      targetType: 'Alert',
      details: 'PEP match detected for customer CUST-789',
      ipAddress: 'Internal',
      severity: 'critical'
    },
    {
      id: 'AUDIT-004',
      timestamp: new Date(Date.now() - 1200000),
      user: 'Sarah Johnson',
      userEmail: 'sarah.johnson@moneytransfer.com',
      action: 'Account Limit Modified',
      targetId: 'CUST-456',
      targetType: 'Customer',
      details: 'Increased daily limit from $5,000 to $10,000',
      ipAddress: '192.168.1.100',
      severity: 'info'
    },
    {
      id: 'AUDIT-005',
      timestamp: new Date(Date.now() - 1800000),
      user: 'Admin User',
      userEmail: 'admin@moneytransfer.com',
      action: 'User Role Changed',
      targetId: 'USER-123',
      targetType: 'User',
      details: 'Updated role from Operator to Senior Operator',
      ipAddress: '192.168.1.102',
      severity: 'warning'
    }
  ];

  const handleLogout = () => {
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/admin-dashboard');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'info':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getActionIcon = (action) => {
    if (action?.includes('Approved')) return 'CheckCircle';
    if (action?.includes('Rejected')) return 'XCircle';
    if (action?.includes('Modified') || action?.includes('Changed')) return 'Edit';
    if (action?.includes('Generated') || action?.includes('Created')) return 'Plus';
    if (action?.includes('Deleted')) return 'Trash2';
    return 'FileText';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={handleBack}
              className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="ml-2">Back to Dashboard</span>
            </button>
            <h1 className="text-3xl font-bold text-foreground">Audit Trail</h1>
            <p className="text-muted-foreground mt-2">
              Complete transaction and system activity logs
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            <Icon name="Download" size={20} />
            <span>Export Logs</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Date Range
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Action Type
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Actions</option>
                <option value="transaction">Transactions</option>
                <option value="document">Documents</option>
                <option value="user">User Management</option>
                <option value="system">System</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by user, action, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Severity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {auditLogs?.map((log) => (
                  <tr key={log?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {formatTimestamp(log?.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="font-medium text-foreground">{log?.user}</p>
                        <p className="text-muted-foreground">{log?.userEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Icon name={getActionIcon(log?.action)} size={16} className="text-primary" />
                        <span className="text-sm font-medium text-foreground">{log?.action}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="font-medium text-foreground">{log?.targetId}</p>
                        <p className="text-muted-foreground">{log?.targetType}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground max-w-xs truncate">
                      {log?.details}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {log?.ipAddress}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                        {log?.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditTrail;